/**
 * Notifications dans l'application — schémas partagés API ↔ front.
 *
 * Le canal a été tranché par le Tech Lead : la notification vit dans
 * l'application. Le courriel reste réservé à ce qui doit sortir de la
 * plateforme, et le SMS a été écarté — le réseau mobile n'est pas plus
 * fiable que la messagerie pendant un délestage.
 */
import { z } from 'zod';
import { schemaPage, schemaPagination } from './administration.js';
/**
 * Ce qu'une notification montre à la personne qui la reçoit.
 *
 * `titre` et `message` sont **figés à l'émission** : une règle dont on change
 * le libellé ne doit pas réécrire les alertes déjà parties. Ils arrivent donc
 * en français, prêts à afficher, sans que le front ait rien à composer.
 */
export const schemaNotification = z.object({
    id: z.uuid(),
    titre: z.string(),
    message: z.string(),
    /**
     * La règle qui l'a produite. Le front s'en sert pour grouper et pour
     * choisir une icône, jamais pour l'afficher telle quelle : « dns.obsolete »
     * ne dit rien à personne (skill `ux-ecrans-afs`).
     */
    regleCode: z.string(),
    /** Vers quoi l'écran renvoie. Un type et un identifiant, jamais une URL. */
    entiteType: z.string().nullable(),
    entiteId: z.uuid().nullable(),
    siteId: z.uuid().nullable(),
    /** `null` tant qu'elle n'est pas lue. */
    lueLe: z.iso.datetime().nullable(),
    creeeLe: z.iso.datetime(),
});
export const schemaFiltresNotifications = schemaPagination.extend({
    /** `true` ne rend que ce qui n'a pas été lu — la file de travail. */
    nonLues: z.coerce.boolean().optional(),
});
/**
 * Ce que la cloche affiche, et ce que le panneau ouvre.
 *
 * Le compteur et les dernières notifications arrivent ENSEMBLE : la cloche
 * seule obligerait à un second appel dès qu'on l'ouvre, et le badge afficherait
 * un nombre que la liste ne confirmerait qu'après.
 */
export const schemaResumeNotifications = z.object({
    nonLues: z.number().int(),
    dernieres: z.array(schemaNotification),
});
/**
 * Marquer comme lu.
 *
 * Une liste d'identifiants plutôt qu'une route par notification : ouvrir le
 * panneau en marque plusieurs d'un coup, et on ne veut pas dix requêtes.
 * Vide ⇒ tout marquer, ce que fait le bouton « Tout marquer comme lu ».
 */
export const schemaMarquageLu = z.object({
    ids: z.array(z.uuid()).max(200).optional(),
});
/**
 * Une page de notifications — la liste complète, sous la cloche.
 *
 * Nommée ici plutôt que composée dans chaque appelant : l'API et le front
 * doivent lire la même enveloppe, et deux compositions séparées finissent
 * par diverger sans que rien n'échoue.
 */
export const schemaPageNotifications = schemaPage(schemaNotification);
//# sourceMappingURL=notifications.js.map